import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

/** One change-history row (cms.history), as returned by the per-record
 *  history endpoints ({count, records}). */
interface HistoryRecord {
  recno: number;
  datetime: string | null;
  userid: string;
  colname: string;
  oldval: string;
  newval: string;
  type: string;
}

interface HistoryResponse {
  count: number;
  records: HistoryRecord[];
}

interface RecordHistoryDrawerProps {
  /** When true, the drawer is mounted, visible, and (re)fetches. */
  isOpen: boolean;
  onClose: () => void;
  /** Full API path for the record's history. null = nothing to fetch. */
  endpoint: string | null;
  /** Drawer heading (e.g. "Trouble Ticket #4821 History"). */
  title: string;
  /** Optional sub-heading under the title (e.g. "PRO 9912"). */
  subtitle?: string;
  /** Map raw colname → friendly label (falls back to the raw colname). */
  fieldLabels?: Record<string, string>;
}

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Compact, read-only history panel for a SINGLE subtable record (trouble
 * ticket or service failure), rendered as a right-side sub-drawer above the
 * editor modal/drawer. Fetches {count, records} from `endpoint` (scoped to one
 * record by dbtable + otherid on the backend) and lists each field change
 * newest-first as `field: old → new` with the user and timestamp.
 */
const RecordHistoryDrawer: React.FC<RecordHistoryDrawerProps> = ({
  isOpen,
  onClose,
  endpoint,
  title,
  subtitle,
  fieldLabels = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<HistoryRecord[]>([]);

  const fetchHistory = useCallback(async () => {
    if (!endpoint) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<HistoryResponse>(endpoint);
      setRecords(res.data?.records ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (isOpen && endpoint) fetchHistory();
  }, [isOpen, endpoint, fetchHistory]);

  // Esc closes the sub-drawer (handled here, above the parent modal's Esc).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    // z-[110] sits above the editor modal/drawer (z-[100]).
    <div
      className="fixed inset-0 z-[110] flex justify-end bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-panel shadow-2xl w-full max-w-2xl h-full flex flex-col border-l border-slate-200 dark:border-gray-700">
        <div className="flex items-start justify-between px-5 py-3 border-b border-slate-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-body flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-gray-400" />
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-muted mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            aria-label="Close history"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          ) : records.length === 0 ? (
            <p className="text-sm text-muted py-8 text-center">
              No history recorded for this record yet.
            </p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="sticky top-0 bg-slate-100 dark:bg-gray-900 text-left text-gray-600 dark:text-gray-300">
                  <th className="px-2 py-1.5 font-semibold border-b border-slate-200 dark:border-gray-700 whitespace-nowrap">Date/Time</th>
                  <th className="px-2 py-1.5 font-semibold border-b border-slate-200 dark:border-gray-700 whitespace-nowrap">User</th>
                  <th className="px-2 py-1.5 font-semibold border-b border-slate-200 dark:border-gray-700">Field</th>
                  <th className="px-2 py-1.5 font-semibold border-b border-slate-200 dark:border-gray-700">Old Value</th>
                  <th className="px-2 py-1.5 font-semibold border-b border-slate-200 dark:border-gray-700">New Value</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.recno}
                    className="align-top border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-900/40"
                  >
                    <td className="px-2 py-1.5 text-muted whitespace-nowrap">
                      {formatDateTime(r.datetime)}
                    </td>
                    <td className="px-2 py-1.5 text-label whitespace-nowrap">
                      {r.userid || '-'}
                    </td>
                    <td className="px-2 py-1.5 font-medium text-body">
                      {fieldLabels[r.colname] ?? r.colname}
                    </td>
                    <td className="px-2 py-1.5 text-muted break-words">
                      {r.oldval || '-'}
                    </td>
                    <td className="px-2 py-1.5 text-body break-words">
                      {r.newval || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RecordHistoryDrawer;
